import { DateRangePickerInput, DateRangePickerInputProps } from '@creditornot/cb-date-picker';
import React from 'react';

type IsoDateRange = {
  from: string; // ISO date, eg. 2021-01-15
  to: string; // ISO date, eg. 2021-01-15
};

export type Props = Omit<DateRangePickerInputProps, 'value' | 'onChange'> & {
  value?: IsoDateRange;
  onChange: (newValue: IsoDateRange | undefined) => void;
};

const isoDateToJsDate = (isoDate: string) => {
  // Parse isoDate manually instead of letting the Date constructor do it, because it considers ISO dates (usually) as
  // UTC while the Date object itself is always in the browser's local timezone. Here, we consider isoDate to be in the
  // browser's local timezone, because DateRangePickerInput works in local timezone.
  //
  // Also, support for ISO strings is not standardized.
  //
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#parameters
  const [yearString, monthString, dayString] = isoDate.split('-');
  const year = parseInt(yearString, 10);
  const monthIndex = parseInt(monthString, 10) - 1;
  const day = parseInt(dayString, 10);
  return new Date(year, monthIndex, day);
};

const jsDateToIsoDate = (jsDate: Date) => {
  // Build the string manually instead of using `Date#toISOString`, because it converts the date to UTC.
  return `${jsDate.getFullYear().toString().padStart(2, '0')}-${(jsDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${jsDate.getDate().toString().padStart(2, '0')}`;
};

const IsoDateRangePickerInput = ({ value, onChange, ...restProps }: Props) => {
  return (
    <DateRangePickerInput
      {...restProps}
      value={value ? { from: isoDateToJsDate(value.from), to: isoDateToJsDate(value.to) } : undefined}
      onChange={newValue => {
        onChange(newValue ? { from: jsDateToIsoDate(newValue.from), to: jsDateToIsoDate(newValue.to) } : undefined);
      }}
    />
  );
};

export default IsoDateRangePickerInput;
